/**
 * Nature world · "Four seasons": one road through spring (L1), summer (L2), autumn (L3) and winter (L4).
 * The sky colour changes per zone (theme.zoneSky); ground, far hills and props follow the season and
 * blend through a soft in-between band at every zone border (ART-BIBLE §5.3).
 * Far = low contrast, no outline; near = saturated, outlined.
 */
import { rolePalette } from '@sketchling/core';
import type { Layer } from '../../Figure';
import { blob, ellipse, poly, scallop } from '../../geom';
import { bush, cloud, flower, grass, pine, ridge, rng, roundTree, spread, twinkle } from '../kit';
import type { WorldArt } from '../types';

const palette = rolePalette({
  hillFar: '#B9D3BE',
  mountainFar: '#C9D6E6',
  snowCap: '#FFFFFF',
  groundSpring: '#A8D58A',
  groundSpSu: '#94CD7C',
  groundSummer: '#7FC46E',
  groundSuAu: '#AEB665',
  groundAutumn: '#DDA562',
  groundAuWi: '#EAD3B4',
  groundWinter: '#F4F7FA',
  meadowLight: '#BEE0A0',
  snowShade: '#DCE7F0',
  cherry: '#F4A3B5',
  cherryLight: '#F8C3CF',
  tree: '#6FAA62',
  maple: '#D9643A',
  mapleLight: '#EBA04A',
  pine: '#3F7A5A',
  snowPine: '#5E8F74',
  trunk: '#8A5A3C',
  bush: '#5E9C5A',
  bushAutumn: '#C98345',
  bushWinter: '#E6EDF3',
  cloud: '#FFFFFF',
  sun: '#F6C453',
  stem: '#4E8A5C',
  grassInk: '#4E8A5C',
  petal: '#F7D35C',
  seed: '#8A5A3C',
  tulip: '#E5675A',
  snow: '#FFFFFF',
  leafFall: '#D9643A',
  mushCap: '#D9554A',
  cream: '#FFF1DC',
  cabin: '#C98E5E',
  roof: '#B9535C',
  window: '#FFD86B',
});

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Vertical band from x0 to x1 with wavy left / right edges (clip for a season's ground). */
function band(x0: number, x1: number, top: number, bottom: number, wob: number, seed: number): string {
  const R = rng(seed);
  const n = 6;
  const left: [number, number][] = [];
  const right: [number, number][] = [];
  for (let i = 0; i <= n; i++) {
    const y = top + ((bottom - top) * i) / n;
    left.push([r2(x0 + (R() - 0.5) * 2 * wob), r2(y)]);
    right.push([r2(x1 + (R() - 0.5) * 2 * wob), r2(y)]);
  }
  return blob([...left.reverse(), ...right], true, 0.5);
}

/** Small snowy mountain (far, no outline). */
const mountain = (x: number, base: number, hw: number, h: number): Layer[] => {
  const top = base - h;
  const body = `M${r2(x - hw)} ${r2(base)}L${r2(x - 6)} ${r2(top + 6)}C${r2(x - 3)} ${r2(top)} ${r2(x + 3)} ${r2(top)} ${r2(x + 6)} ${r2(top + 6)}L${r2(x + hw)} ${r2(base)}Z`;
  const cap = `M${r2(x - hw * 0.34)} ${r2(top + h * 0.34)}L${r2(x - 6)} ${r2(top + 6)}C${r2(x - 3)} ${r2(top)} ${r2(x + 3)} ${r2(top)} ${r2(x + 6)} ${r2(top + 6)}L${r2(x + hw * 0.34)} ${r2(top + h * 0.34)}C${r2(x + hw * 0.18)} ${r2(top + h * 0.42)} ${r2(x + hw * 0.08)} ${r2(top + h * 0.3)} ${r2(x)} ${r2(top + h * 0.4)}C${r2(x - hw * 0.1)} ${r2(top + h * 0.3)} ${r2(x - hw * 0.2)} ${r2(top + h * 0.42)} ${r2(x - hw * 0.34)} ${r2(top + h * 0.34)}Z`;
  return [
    { k: 'region', d: body, fill: 'mountainFar', shade: false, line: false },
    { k: 'region', d: cap, fill: 'snowCap', shade: false, line: false },
  ];
};

/** Little snowy cabin standing on (x, y). */
const cabin = (x: number, y: number, s: number): Layer[] => [
  { k: 'region', d: poly([[x - 16 * s, y], [x - 16 * s, y - 16 * s], [x, y - 30 * s], [x + 16 * s, y - 16 * s], [x + 16 * s, y]]), fill: 'cabin', shade: 4 * s },
  { k: 'region', d: poly([[x - 21 * s, y - 14 * s], [x, y - 34 * s], [x + 21 * s, y - 14 * s], [x + 17 * s, y - 12 * s], [x, y - 28 * s], [x - 17 * s, y - 12 * s]]), fill: 'snow', shade: false },
  { k: 'region', d: poly([[x - 4 * s, y], [x - 4 * s, y - 9 * s], [x + 4 * s, y - 9 * s], [x + 4 * s, y]]), fill: 'roof', shade: false },
  { k: 'spot', d: ellipse(x + 9 * s, y - 9 * s, 3 * s, 3 * s), fill: 'window', stage: 2 },
];

/** Tiny toadstool. */
const toadstool = (x: number, y: number, s: number): Layer[] => [
  { k: 'region', d: poly([[x - 2.5 * s, y], [x - 2 * s, y - 6 * s], [x + 2 * s, y - 6 * s], [x + 2.5 * s, y]]), fill: 'cream', shade: false },
  { k: 'region', d: `M${r2(x - 7 * s)} ${r2(y - 5 * s)}C${r2(x - 7 * s)} ${r2(y - 12 * s)} ${r2(x + 7 * s)} ${r2(y - 12 * s)} ${r2(x + 7 * s)} ${r2(y - 5 * s)}Z`, fill: 'mushCap', shade: false },
];

export const NATURE_ART: WorldArt = {
  id: 'nature',
  palette,
  theme: {
    sky: '#CFEAF5',
    skyLow: '#FBF4E4',
    zoneSky: ['#E3F1DA', '#CFEAF5', '#F6DFC2', '#E4ECF4'],
    ink: '#33402C',
    label: '#FFFDF6',
    accents: ['#F4A3B5', '#F6C453', '#D9643A'],
    trail: { kind: 'dirt', color: '#E8D2A8', edge: '#C4A574', deco: '#D6BC8C' },
    card: { bg: '#E8F2DF', border: '#CFE2C2', shadow: '#BFD6B0' },
    node: { ring: '#E9783A', done: '#4CC38A', disc: '#FFFDF6', locked: '#E0DCCF' },
    // nature.webp: the painted road winds through the lower middle (≈ 55-72 % of the height).
    raster: { mid: 0.68, amp: 0.06, scrim: 'rgba(255,250,240,0.12)', portrait: { mid: 0.74, amp: 0.06 } },
  },
  scene: ({ w, h, zones }) => {
    const s = h / 300;
    const R = rng(21);
    const hz = h * 0.38;
    const L: Layer[] = [];
    const z = (i: number) => zones[Math.min(i, zones.length - 1)] ?? [0, w];
    const ground = ['groundSpring', 'groundSummer', 'groundAutumn', 'groundWinter'];
    const blend = ['groundSpSu', 'groundSuAu', 'groundAuWi'];
    const bushes = ['bush', 'bush', 'bushAutumn', 'bushWinter'];
    const zoneAt = (x: number) => Math.max(0, zones.findIndex(([a, b]) => x >= a && x < b));

    // sky: summer sun, clouds everywhere
    L.push({ k: 'region', d: ellipse((z(1)[0] + z(1)[1]) / 2, hz - 86 * s, 26 * s), fill: 'sun', shade: false, line: false });
    for (const x of spread(60, w, 280 * s, 4)) L.push(...cloud(x, hz - (40 + R() * 50) * s, 0.8 + R() * 0.4));
    // far: snowy mountains behind winter (and late autumn), soft hills elsewhere
    for (const x of spread(z(2)[1] - 120 * s, w + 60, 120 * s, 9)) L.push(...mountain(x, hz + 6 * s, (70 + R() * 30) * s, (70 + R() * 30) * s));
    L.push({ k: 'region', d: ridge(w, hz, 14 * s, 190 * s, 8, h + 40), fill: 'hillFar', shade: false, line: false });
    // season grounds + soft blended borders
    const groundD = ridge(w, hz + 34 * s, 6 * s, 200 * s, 30, h + 40);
    zones.forEach(([x0, x1], i) => {
      L.push({ k: 'region', d: groundD, fill: ground[i]!, shade: false, line: true, clip: band(i ? x0 : -200, i === zones.length - 1 ? w + 200 : x1 + 2, -10, h + 60, 18 * s, 40 + i) });
    });
    zones.forEach(([x0], i) => {
      if (!i) return;
      L.push({ k: 'region', d: groundD, fill: blend[i - 1]!, shade: false, line: false, clip: band(x0 - 34 * s, x0 + 34 * s, -10, h + 60, 16 * s, 60 + i) });
    });
    // light meadow patches (spring + summer), snow drifts (winter)
    for (const x of spread(0, w, 300 * s, 17)) {
      const i = zoneAt(x);
      if (i === 2) continue;
      L.push({ k: 'region', d: scallop(x, h * 0.98, 120 * s, 22 * s, 7, 0.4, 1.1), fill: i === 3 ? 'snowShade' : 'meadowLight', shade: false, line: false });
    }
    // horizon trees per season
    zones.forEach(([x0, x1], i) => {
      for (const x of spread(x0 + 10, x1 - 10, 60 * s, 50 + i)) {
        const y = hz + 40 * s + R() * 10 * s;
        if (i === 0) L.push(...roundTree(x, y, (0.85 + R() * 0.3) * s, R() < 0.6 ? 'cherry' : 'cherryLight'));
        else if (i === 1) R() < 0.6 && L.push(...roundTree(x, y, (0.85 + R() * 0.3) * s, 'tree'));
        else if (i === 2) L.push(...roundTree(x, y, (0.85 + R() * 0.35) * s, R() < 0.5 ? 'maple' : 'mapleLight'));
        else L.push(...pine(x, y + 4 * s, (0.8 + R() * 0.4) * s, 'snowPine'));
      }
    });
    // summer sunflower row and the winter cabin, on the horizon
    for (const x of spread(z(1)[0] + 30 * s, z(1)[1] - 30 * s, 22 * s, 33)) if (R() < 0.7) L.push(...flower(x, hz + 46 * s + R() * 6 * s, 'petal', 'seed', 1.5 * s));
    L.push(...cabin(z(3)[0] + (z(3)[1] - z(3)[0]) * 0.55, hz + 50 * s, 1.1 * s));
    // foreground: below the node band (or the very top of the ground)
    zones.forEach(([x0, x1], i) => {
      for (const x of spread(x0 + 10, x1 - 10, 30 * s, 70 + i)) {
        const y = h * (0.74 + R() * 0.24);
        if (i === 0) L.push(...flower(x, y, R() < 0.5 ? 'tulip' : 'cherry', 'petal', s));
        else if (i === 1) R() < 0.55 ? L.push(...flower(x, y, 'petal', 'seed', 1.5 * s)) : L.push(...grass(x, y, s));
        else if (i === 2) {
          const c = R();
          if (c < 0.45) L.push({ k: 'spot', d: scallop(x, y, 5 * s, 3 * s, 5, R() * 3, 1.6), fill: R() < 0.5 ? 'leafFall' : 'mapleLight', stage: 2 });
          else if (c < 0.6) L.push(...toadstool(x, y, 1.2 * s));
        } else L.push(twinkle(x, y - h * 0.62 * R(), 3.2 * s, 'snow'));
      }
      if (i < 3) for (const x of spread(x0 + 20, x1 - 20, 90 * s, 90 + i)) L.push(...grass(x, h * (0.8 + R() * 0.16), s));
    });
    // bushes cover the season borders at the bottom edge
    zones.forEach(([x0], i) => {
      if (i) L.push(...bush(x0 - 14 * s, h + 6 * s, 1.5 * s, bushes[i - 1]), ...bush(x0 + 22 * s, h + 10 * s, 1.3 * s, bushes[i]));
    });
    return { vb: [w, h], layers: L, guides: [] };
  },
};
